/**
 * Min plan: the employee's own schedule (owner's employee UI brief, 2026-09-22, screen 2). Week
 * first, one row per day that tells an own shift, an absence, an open shift and a free day apart;
 * each own shift opens Vaktdetaljer, an open shift opens its own detail and is asked for only by
 * its button. The month is an overview: marks per day, and a day opens on a click.
 */
import {
  type ApiAbsence,
  type ApiShift,
  useMyAbsences,
  useMyShifts,
  useMySwaps,
  useOpenShifts,
  usePunchStatus,
} from '@/api/data';
import { useAuth } from '@/auth/store';
import { T } from '@/i18n';
import { oslo, today } from '@/rules';
import { useTenant } from '@/shell/tenant';
import { Chip, Drawer, LoadError, Menu, Skeleton } from '@/ui';
import { DateJump } from '@/ui/DateJump';
import { DN, DNL, WeekNav, fmtD, fmtDs, fullName, num, typeName } from '@/ui/format';
import { Icon } from '@/ui/icons';
import { PrintSheet, shiftLine, usePrint } from '@/ui/print';
import { addDays, dow, inRange, mondayOf } from '@timefly/core';
import { useState } from 'react';
import { catInfo, catOf } from '../fravaer/category';
import {
  Card,
  OpenDrawer,
  OpenRow,
  ShiftDrawer,
  ShiftRow,
  SwapList,
  dayOf,
  lengthH,
  shiftName,
  useAsk,
} from './parts';

const absDay = (a: ApiAbsence, d: string) =>
  a.status !== 'declined' &&
  inRange(d, oslo(a.start_date)?.date ?? '', oslo(a.end_date)?.date ?? oslo(a.start_date)?.date ?? '');

/** An absence on a day, in its category's colour and icon, with its state in words. */
export function AbsenceLine({ a }: { a: ApiAbsence }) {
  const c = catInfo(catOf(a.absence_type));
  return (
    <div
      className="eabs"
      data-testid="plan-absence"
      style={{
        background: `var(--${c.tone}-f)`,
        borderColor: `var(--${c.tone}-l)`,
      }}
    >
      <span className="eabsic" style={{ color: `var(--${c.tone}-i)` }} aria-hidden="true">
        <Icon name={c.icon} />
      </span>
      <span className="ebody">
        <b>{typeName(a.absence_type)}</b>
        <span>{T('Hele dagen')}</span>
      </span>
      {a.status === 'accepted' ? (
        <Chip tone="ok" small>
          {T('Godkjent')}
        </Chip>
      ) : (
        <Chip tone="warn" small>
          {T('Venter')}
        </Chip>
      )}
    </div>
  );
}

const lastOf = (ym: string) => {
  const [y, m] = ym.split('-').map(Number);
  return `${ym}-${String(new Date(Date.UTC(y ?? 2026, m ?? 1, 0)).getUTCDate()).padStart(2, '0')}`;
};
const stepYm = (ym: string, n: number) => {
  const [y, m] = ym.split('-').map(Number);
  return new Date(Date.UTC(y ?? 2026, (m ?? 1) - 1 + n, 1)).toISOString().slice(0, 7);
};

export default function Plan() {
  const user = useAuth((s) => s.user);
  const { tenant, workspace } = useTenant();
  const day0 = today();
  // nothing module-bound is asked for before the company has answered
  const scheduling = !!tenant && workspace.enabled.scheduling;
  const [mode, setMode] = useState<'uke' | 'maaned'>('uke');
  const [monday, setMonday] = useState(() => mondayOf(day0));
  const [ym, setYm] = useState(day0.slice(0, 7));
  const [picked, setPicked] = useState<string | null>(null);
  const [detail, setDetail] = useState<ApiShift | null>(null);
  const [openDetail, setOpenDetail] = useState<ApiShift | null>(null);
  const a = useAsk();
  const printer = usePrint();

  const first = mode === 'uke' ? monday : `${ym}-01`;
  const last = mode === 'uke' ? addDays(monday, 6) : lastOf(ym);
  const shiftsQ = useMyShifts(first, last);
  const absQ = useMyAbsences(first, last);
  const openQ = useOpenShifts(first, last, scheduling);
  const statusQ = usePunchStatus();
  const swapsQ = useMySwaps();

  const mine = (shiftsQ.data?.shifts ?? [])
    .filter((s) => s.user_id === user?.id)
    .sort((x, y) => x.start_time.localeCompare(y.start_time));
  const abs = absQ.data ?? [];
  const open = scheduling ? (openQ.data ?? []).filter((s) => dayOf(s) >= day0) : [];
  const onDay = (d: string) => ({
    shifts: mine.filter((s) => dayOf(s) === d),
    abs: abs.filter((x) => absDay(x, d)),
    open: open.filter((s) => dayOf(s) === d),
  });
  const days = Array.from({ length: 7 }, (_, i) => addDays(monday, i));
  const weekShifts = mine.filter((s) => dayOf(s) >= monday && dayOf(s) <= addDays(monday, 6));
  const planned = weekShifts.reduce((sum, s) => sum + lengthH(s), 0);
  const absDays = days.filter((d) => abs.some((x) => x.status === 'accepted' && absDay(x, d))).length;
  const swaps = (swapsQ.data ?? []).filter(
    (x) => x.offered_by_one === user?.id || x.offered_by_two === user?.id,
  );
  const shown = detail ? (mine.find((s) => s.id === detail.id) ?? detail) : null;
  const todays = mine.filter((s) => dayOf(s) === day0);

  const printDays =
    mode === 'uke'
      ? days
      : Array.from({ length: Number(lastOf(ym).slice(8)) }, (_, i) => addDays(`${ym}-01`, i));
  const printCells = (list: ApiShift[]) =>
    new Map(
      printDays.map((d) => [d, list.filter((s) => dayOf(s) === d).map((s) => shiftLine(s, shiftName(s)))]),
    );

  return (
    <>
      {printer.on && (
        <PrintSheet
          title={T('Min plan')}
          company={tenant?.name ?? ''}
          days={printDays}
          portrait={mode === 'maaned'}
          rows={[
            {
              key: 'me',
              name: user ? fullName(user) : '',
              cells: printCells(mine),
            },
            ...(open.length ? [{ key: 'open', name: T('Ledig'), cells: printCells(open) }] : []),
          ]}
        />
      )}
      <div className="ph">
        <div>
          <h1>{T('Min plan')}</h1>
          <div className="sub">
            {user ? fullName(user) : ''}, {tenant?.name ?? ''}
          </div>
        </div>
        <span className="sp" />
        <fieldset className="seg" aria-label={T('Visning')}>
          <button type="button" aria-pressed={mode === 'uke'} onClick={() => setMode('uke')}>
            {T('Uke')}
          </button>
          <button type="button" aria-pressed={mode === 'maaned'} onClick={() => setMode('maaned')}>
            {T('Måned')}
          </button>
        </fieldset>
        {mode === 'uke' ? (
          <WeekNav monday={monday} onChange={setMonday} />
        ) : (
          <span className="wk">
            <button type="button" onClick={() => setYm(stepYm(ym, -1))} aria-label={T('Forrige måned')}>
              <Icon name="chevronLeft" />
            </button>
            <DateJump month value={ym} onChange={setYm}>
              <span className="lbl">
                {new Date(`${ym}-01T12:00:00Z`).toLocaleDateString('nb-NO', {
                  month: 'long',
                  year: 'numeric',
                })}
              </span>
            </DateJump>
            <button type="button" onClick={() => setYm(stepYm(ym, 1))} aria-label={T('Neste måned')}>
              <Icon name="chevronRight" />
            </button>
          </span>
        )}
        {/* my own week or month on paper (issue #49) */}
        <Menu
          trigger={
            <button type="button" className="btn" aria-label={T('Mer')}>
              ⋯
            </button>
          }
          items={[{ key: 'print', label: T('Skriv ut'), onSelect: printer.print }]}
        />
      </div>

      {mode === 'uke' ? (
        <div className="egrid">
          <section className="card2 eweek" data-testid="week">
            {shiftsQ.isError || absQ.isError ? (
              // a failed fetch says so and retries; it is never «Ingen vakter» (#272 M-02)
              <div className="cb">
                <LoadError
                  what={shiftsQ.isError ? T('vaktene dine') : T('fraværet ditt')}
                  onRetry={() => {
                    if (shiftsQ.isError) shiftsQ.refetch();
                    if (absQ.isError) absQ.refetch();
                  }}
                />
              </div>
            ) : shiftsQ.isPending ? (
              <div className="cb">
                <Skeleton />
              </div>
            ) : (
              days.map((d) => {
                const x = onDay(d);
                const empty = !x.shifts.length && !x.abs.length && !x.open.length;
                return (
                  <div className={`eday${d === day0 ? ' today' : ''}`} key={d} data-testid={`day-${d}`}>
                    <span className="edh">
                      <small>{DN[dow(d)]}</small>
                      <b>{fmtDs(d)}</b>
                    </span>
                    <div className="edc">
                      {x.abs.map((ab) => (
                        <AbsenceLine key={ab.id} a={ab} />
                      ))}
                      {x.shifts.map((s) => (
                        <ShiftRow
                          key={s.id}
                          s={s}
                          place={tenant?.name}
                          onOpen={() => setDetail(s)}
                          showDate={false}
                        />
                      ))}
                      {x.open.map((s) => (
                        <OpenRow key={s.id} s={s} a={a} onOpen={() => setOpenDetail(s)} showDate={false} />
                      ))}
                      {empty && <span className="efree">{T('Ingen vakter')}</span>}
                    </div>
                  </div>
                );
              })
            )}
          </section>
          <div>
            <Card
              title={T('Denne uken')}
              sub={`${fmtDs(monday)}–${fmtD(addDays(monday, 6))}`}
              testid="week-summary"
            >
              <div className="ekv">
                <Icon name="clock" />
                <span>{T('Planlagte timer')}</span>
                {/* unknown is never 0 t: «–» until each source has answered (#272 B-31) */}
                <b>{shiftsQ.isSuccess ? `${num(planned, 1)} t` : '–'}</b>
              </div>
              <div className="ekv">
                <Icon name="calendar" />
                <span>{T('Antall vakter')}</span>
                <b>{shiftsQ.isSuccess ? weekShifts.length : '–'}</b>
              </div>
              {scheduling && (
                <div className="ekv">
                  <Icon name="users" />
                  <span>{T('Ledige vakter')}</span>
                  <b>
                    {openQ.isSuccess
                      ? open.filter((s) => dayOf(s) >= monday && dayOf(s) <= addDays(monday, 6)).length
                      : '–'}
                  </b>
                </div>
              )}
              <div className="ekv">
                <Icon name="sun" />
                <span>{T('Fraværsdager')}</span>
                <b>{absQ.isSuccess ? absDays : '–'}</b>
              </div>
            </Card>
            {swaps.length > 0 && (
              <Card title={T('Vaktbytter')} sub={T('Dine forslag og svar')} testid="swaps">
                <SwapList swaps={swaps} />
              </Card>
            )}
            {scheduling && (
              <p className="hint">
                {T('Ledige vakter tas med knappen Ta vakt. Lederen velger hvem som får vakten.')}
              </p>
            )}
          </div>
        </div>
      ) : (
        <section className="card2" data-testid="month">
          <div className="cb">
            {/* an empty month on a failed fetch reads as «no shifts» (#272 B-31) */}
            {(shiftsQ.isError || absQ.isError) && (
              <LoadError
                what={shiftsQ.isError ? T('vaktene dine') : T('fraværet ditt')}
                onRetry={() => {
                  if (shiftsQ.isError) shiftsQ.refetch();
                  if (absQ.isError) absQ.refetch();
                }}
              />
            )}
            <div className="month">
              {DN.map((n) => (
                <div key={n} className="mh">
                  {n}
                </div>
              ))}
              {Array.from({ length: 42 }, (_, i) => addDays(mondayOf(`${ym}-01`), i))
                .filter((d, i) => i < 35 || d <= lastOf(ym))
                .map((d) => {
                  const inMonth = d.startsWith(ym);
                  const x = onDay(d);
                  const marks = x.shifts.length + x.abs.length + x.open.length;
                  const label = [
                    `${DNL[dow(d)]} ${fmtD(d)}`,
                    x.shifts.length ? T('{n} vakter', { n: x.shifts.length }) : '',
                    x.abs.length ? typeName(x.abs[0]?.absence_type) : '',
                    x.open.length ? T('{n} ledige', { n: x.open.length }) : '',
                  ]
                    .filter(Boolean)
                    .join(', ');
                  return (
                    <button
                      type="button"
                      key={d}
                      className={`md${inMonth ? '' : ' out'}${d === day0 ? ' today' : ''}${dow(d) >= 5 ? ' we' : ''}`}
                      onClick={() => setPicked(d)}
                      aria-label={label}
                      disabled={!marks}
                    >
                      <span className="dn">{Number(d.slice(8, 10))}</span>
                      {x.abs[0] && (
                        <span
                          className="ma"
                          style={{
                            background: `var(--${catInfo(catOf(x.abs[0].absence_type)).tone}-l)`,
                          }}
                        >
                          {typeName(x.abs[0].absence_type)}
                        </span>
                      )}
                      {x.shifts.map((s) => (
                        <span key={s.id} className={`ms${s.published ? '' : ' draft'}`}>
                          {oslo(s.start_time)?.time}
                        </span>
                      ))}
                      {x.open.length > 0 && (
                        <span className="mo">{T('{n} ledig', { n: x.open.length })}</span>
                      )}
                    </button>
                  );
                })}
            </div>
            <p className="hint">
              {T('Trykk på en dag for detaljer.')}{' '}
              {num(
                mine.reduce((s, x) => s + lengthH(x), 0),
                1,
              )}{' '}
              t {T('planlagt')}.
            </p>
          </div>
        </section>
      )}

      <Drawer
        open={!!picked}
        onClose={() => setPicked(null)}
        title={picked ? `${DNL[dow(picked)]} ${fmtD(picked)}` : ''}
      >
        {picked && (
          <div data-testid="day-detail">
            {onDay(picked).abs.map((ab) => (
              <AbsenceLine key={ab.id} a={ab} />
            ))}
            {onDay(picked).shifts.map((s) => (
              <ShiftRow
                key={s.id}
                s={s}
                place={tenant?.name}
                onOpen={() => {
                  setPicked(null);
                  setDetail(s);
                }}
              />
            ))}
            {onDay(picked).open.map((s) => (
              <OpenRow
                key={s.id}
                s={s}
                a={a}
                onOpen={() => {
                  setPicked(null);
                  setOpenDetail(s);
                }}
              />
            ))}
          </div>
        )}
      </Drawer>
      <ShiftDrawer shift={shown} onClose={() => setDetail(null)} st={statusQ.data} todays={todays} />
      <OpenDrawer s={openDetail} a={a} onClose={() => setOpenDetail(null)} />
    </>
  );
}
